import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, BookOpen, Users,
  ChevronRight, Edit3, Trash2, Clock,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { modulesAPI, usersAPI } from '../../services/api';
import { MODULE_CATEGORIES } from '../../constants/enums';
import './ModuleCreation.css';

const ModuleCreation = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [instructors, setInstructors] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    level: 'Beginner',
    status: 'Active',
    instructorIds: [],
  });
  const [moduleCategories, setModuleCategories] = useState(MODULE_CATEGORIES);

  const { showNotification, currentUser } = useAppContext();
  const {
    filteredModules,
    personalAnalytics,
    canCreateModule,
    canEditModule,
    canDeleteModule,
    refreshData,
    loading,
  } = useRoleData();

  // Fetch instructors for admin assignment
  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const response = await usersAPI.getUsers();
        const allUsers = response.data.data || response.data;
        const instructorList = allUsers.filter(user => user.role === 'Instructor');
        setInstructors(instructorList);
      } catch (error) {
        console.error('Failed to fetch instructors:', error);
        showNotification('Failed to load instructors list', 'error');
      }
    };

    if (currentUser?.role === 'Administrator') {
      fetchInstructors();
    }
  }, [currentUser, showNotification]);

  // Fetch module categories from API
  useEffect(() => {
    const fetchModuleCategories = async () => {
      try {
        const response = await modulesAPI.getCategories();
        const fetchedCategories = response.data.data;
        if (fetchedCategories && fetchedCategories.length > 0) {
          setModuleCategories(fetchedCategories);
        }
      } catch (error) {
        console.error('Failed to fetch module categories:', error);
        // Keep default categories
      }
    };
    fetchModuleCategories();
  }, []);

  // Reset form when create modal opens
  useEffect(() => {
    if (modalOpen && !editModalOpen) {
      setForm({
        title: '',
        description: '',
        category: '',
        level: 'Beginner',
        status: 'Active',
        instructorIds: [],
      });
    }
  }, [modalOpen, editModalOpen]);

  const filtered = filteredModules.filter(
    (m) =>
      m.title.toLowerCase().includes(search.toLowerCase()) ||
      m.description.toLowerCase().includes(search.toLowerCase())
  );

  const traineeModuleProgress = new Map(
    (personalAnalytics?.moduleProgress || []).map((module) => [module.moduleId, Number(module.progress) || 0])
  );

  const getModuleCompletionValue = (module) => {
    if (currentUser?.role === 'Trainee') {
      return traineeModuleProgress.get(module.id) ?? (Number(module.completionRate) || 0);
    }

    return Number(module.completionRate) || 0;
  };

  const completionLabel = currentUser?.role === 'Trainee' ? 'My Progress' : 'Completion Rate';
  const averageCompletionValue = currentUser?.role === 'Trainee'
    ? Number(personalAnalytics?.overallProgress) || 0
    : (filteredModules.length > 0
      ? Math.round(filteredModules.reduce((sum, module) => sum + getModuleCompletionValue(module), 0) / filteredModules.length)
      : 0);

  const levelColor = { Beginner: 'badge-green', Intermediate: 'badge-yellow', Advanced: 'badge-blue' };

  // Summary cards - exclude Draft for Trainees
  const summaryCards = [
    { label: 'Total Modules', value: filteredModules.length, color: 'green' },
    { label: 'Active', value: filteredModules.filter((m) => m.status === 'Active').length, color: 'blue' },
    ...(currentUser?.role !== 'Trainee' ? [{ label: 'Draft', value: filteredModules.filter((m) => m.status === 'Draft').length, color: 'yellow' }] : []),
    { label: currentUser?.role === 'Trainee' ? 'Overall Progress' : 'Avg. Completion', value: `${averageCompletionValue}%`, color: 'dark' },
  ];

  const handleChange = (e) => {
    const { name, value, type, selectedOptions } = e.target;
    if (type === 'select-multiple') {
      const selectedValues = Array.from(selectedOptions).map(option => parseInt(option.value));
      setForm((prev) => ({ ...prev, [name]: selectedValues }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCreateModule = async () => {
    // Basic validation
    if (!form.title.trim() || !form.description.trim()) {
      showNotification('Title and description are required', 'error');
      return;
    }

    // Admin must assign at least one instructor
    if (currentUser?.role === 'Administrator' && (!form.instructorIds || form.instructorIds.length === 0)) {
      showNotification('At least one instructor must be assigned', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const data = {
        title: form.title,
        description: form.description,
        category: form.category || null,
        level: form.level,
        status: form.status,
      };
      // Include instructorIds for admin only
      if (currentUser?.role === 'Administrator' && form.instructorIds && form.instructorIds.length > 0) {
        data.instructorIds = form.instructorIds;
      }
      await modulesAPI.createModule(data);
      showNotification(`Module "${form.title}" created successfully!`, 'success');
      setModalOpen(false);
      // Reset form
      setForm({
        title: '',
        description: '',
        category: '',
        level: 'Beginner',
        status: 'Active',
        instructorIds: [],
      });
      // Refresh module list
      refreshData();
    } catch (error) {
      console.error('Failed to create module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to create module. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditModule = (module) => {
    if (!canEditModule(module)) {
      showNotification('You do not have permission to edit this module', 'error');
      return;
    }
    setEditingModule(module);
    setForm({
      title: module.title,
      description: module.description,
      category: module.category,
      level: module.level,
      status: module.status,
      instructorIds: module.instructorIds || [],
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    // Basic validation
    if (!form.title.trim() || !form.description.trim()) {
      showNotification('Title and description are required', 'error');
      return;
    }

    if (!editingModule) return;

    setSubmitting(true);
    try {
      await modulesAPI.updateModule(editingModule.id, {
        title: form.title,
        description: form.description,
        category: form.category || null,
        level: form.level,
        status: form.status,
      });
      showNotification(`Module "${form.title}" updated successfully!`, 'success');
      setEditModalOpen(false);
      setEditingModule(null);
      // Refresh module list
      refreshData();
    } catch (error) {
      console.error('Failed to update module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to update module. Please try again.',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteModule = (module) => {
    if (!canDeleteModule(module)) {
      showNotification('You do not have permission to delete this module', 'error');
      return;
    }

    setModuleToDelete(module);
    setDeleteConfirmationText('');
    setDeleteConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!moduleToDelete) return;

    // Validate confirmation text
    if (deleteConfirmationText.trim() !== 'delete module') {
      showNotification('Please type "delete module" exactly to confirm deletion', 'error');
      return;
    }

    setDeletingId(moduleToDelete.id);
    try {
      await modulesAPI.deleteModule(moduleToDelete.id);
      showNotification(`Module "${moduleToDelete.title}" deleted successfully!`, 'success');
      // Refresh module list
      refreshData();
      setDeleteConfirmModalOpen(false);
      setModuleToDelete(null);
      setDeleteConfirmationText('');
    } catch (error) {
      console.error('Failed to delete module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to delete module. Please try again.',
        'error'
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="module-creation">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>{currentUser?.role === 'Instructor' ? 'My Modules' : currentUser?.role === 'Trainee' ? 'My Training' : 'Modules'}</h1>
          <p>
            {currentUser?.role === 'Instructor' ? 'View and manage modules assigned to you' :
             currentUser?.role === 'Trainee' ? 'View modules you are enrolled in' :
             'Create and manage training module collections'}
          </p>
        </div>
        {canCreateModule && (
          <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
            <Plus size={15} />
            Create Module
          </button>
        )}
      </div>

      {loading ? (
        <div className="module-creation__loading">
          <div className="module-creation__spinner"></div>
          <p>Loading modules...</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="module-creation__summary">
            {summaryCards.map(({ label, value, color }) => (
              <div key={label} className={`module-creation__summary-card module-creation__summary-card--${color}`}>
                <span className="module-creation__summary-value">{value}</span>
                <span className="module-creation__summary-label">{label}</span>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="card module-creation__search-bar">
            <div className="content-lib__search" style={{ flex: 1 }}>
              <Search size={16} className="content-lib__search-icon" />
              <input
                className="content-lib__search-input"
                type="text"
                placeholder="Search modules..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Module Cards */}
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><BookOpen size={28} /></div>
              <h3>No modules found</h3>
              <p>
                {currentUser?.role === 'Instructor' ? 'You are not assigned to any modules yet' :
                 currentUser?.role === 'Trainee' ? 'You are not enrolled in any modules yet' :
                 'Create your first module or adjust the search'}
              </p>
              {canCreateModule && (
                <button className="btn btn-primary" onClick={() => setModalOpen(true)}>
                  <Plus size={15} /> Create Module
                </button>
              )}
            </div>
          ) : (
            <div className="module-creation__grid">
              {filtered.map((m) => {
                const completionValue = getModuleCompletionValue(m);

                return (
                <div
                  key={m.id}
                  className="module-creation__card card"
                  onClick={() => navigate(`/modules/${m.moduleId ? m.moduleId.replace(/-/g, '') : m.id}`, { state: { fromList: true } })}
                >
                  <div className="module-creation__card-header">
                    <div className="module-creation__card-icon">
                      <BookOpen size={20} />
                    </div>
                    <div className="module-creation__card-badges">
                      <span className={`badge ${levelColor[m.level] || 'badge-gray'}`}>{m.level}</span>
                      <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                        {m.status}
                      </span>
                    </div>
                  </div>

                  <h3 className="module-creation__card-title">{m.title}</h3>
                  <p className="module-creation__card-desc">{m.description}</p>

                  <div className="module-creation__card-stats">
                    <div className="module-creation__stat">
                      <BookOpen size={13} />
                      <span>{m.contentIds.length} content</span>
                    </div>
                    <div className="module-creation__stat">
                      <Users size={13} />
                      <span>{m.traineeIds.length} enrolled</span>
                    </div>
                    <div className="module-creation__stat">
                      <Clock size={13} />
                      <span>{m.duration} min</span>
                    </div>
                  </div>

                  <div className="module-creation__progress">
                    <div className="module-creation__progress-header">
                      <span>{completionLabel}</span>
                      <span className="module-creation__progress-pct">{completionValue}%</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: `${completionValue}%` }} />
                    </div>
                  </div>

                  <div className="module-creation__card-footer">
                    <div className="module-creation__card-actions" onClick={(e) => e.stopPropagation()}>
                      {canEditModule(m) && (
                        <button className="btn-icon" title="Edit module" onClick={() => handleEditModule(m)}>
                          <Edit3 size={15} />
                        </button>
                      )}
                      {canDeleteModule(m) && (
                        <button
                          className="btn-icon"
                          title="Delete module"
                          style={{ color: '#dc2626', opacity: deletingId === m.id ? 0.5 : 1 }}
                          onClick={() => handleDeleteModule(m)}
                          disabled={deletingId === m.id}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                    <button className="module-creation__view-btn">
                      View Details <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Create Module Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Create New Module"
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={submitting}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreateModule} disabled={submitting}>
              {submitting ? 'Creating...' : <><Plus size={14} /> Create Module</>}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="form-group">
            <label className="form-label">Module Title *</label>
            <input
              className="form-input"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g., Advanced Safety Training"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              className="form-textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the training objectives..."
              rows={3}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                <option value="">Select category</option>
                {moduleCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Level</label>
              <select className="form-select" name="level" value={form.level} onChange={handleChange}>
                {['Beginner', 'Intermediate', 'Advanced'].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>
          {currentUser?.role === 'Administrator' && (
            <div className="form-group">
              <label className="form-label">Assign Instructors *</label>
              {instructors.length === 0 ? (
                <div className="form-help text-warning">
                  No instructors available. Please create instructor accounts first.
                </div>
              ) : (
                <>
                  <select
                    className="form-select"
                    name="instructorIds"
                    multiple
                    value={(form.instructorIds || []).map(id => id.toString())}
                    onChange={handleChange}
                    size={Math.min(5, instructors.length)}
                    required
                  >
                    <option value="" disabled>Select one or more instructors</option>
                    {instructors.map(instructor => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.name} ({instructor.email})
                      </option>
                    ))}
                  </select>
                  <div className="form-help">
                    Hold Ctrl (or Cmd) to select multiple instructors. At least one instructor must be assigned.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Edit Module Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingModule(null);
        }}
        title="Edit Module"
        size="md"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setEditModalOpen(false)} disabled={submitting}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={submitting}>
              {submitting ? 'Saving...' : <><Edit3 size={14} /> Save Changes</>}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="form-group">
            <label className="form-label">Module Title *</label>
            <input
              className="form-input"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g., Advanced Safety Training"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              className="form-textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe the training objectives..."
              rows={3}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-select" name="category" value={form.category} onChange={handleChange}>
                <option value="">Select category</option>
                {moduleCategories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Level</label>
              <select className="form-select" name="level" value={form.level} onChange={handleChange}>
                {['Beginner', 'Intermediate', 'Advanced'].map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete Module Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModalOpen}
        onClose={() => {
          setDeleteConfirmModalOpen(false);
          setModuleToDelete(null);
          setDeleteConfirmationText('');
        }}
        title="Delete Module"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => {
              setDeleteConfirmModalOpen(false);
              setModuleToDelete(null);
              setDeleteConfirmationText('');
            }}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletingId}>
              {deletingId ? 'Deleting...' : 'Delete Module'}
            </button>
          </div>
        }
      >
        {moduleToDelete && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p>
              You are about to delete <strong>{moduleToDelete.title}</strong>. This action cannot be undone.
            </p>
            <p>
              This will permanently remove the module and all associated data including trainee enrollments and progress tracking.
            </p>
            <div className="form-group">
              <label className="form-label">
                Type <strong>"delete module"</strong> to confirm:
              </label>
              <input
                className="form-input"
                type="text"
                placeholder="Type 'delete module' here"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                autoFocus
              />
              <small className="text-muted">
                This extra step prevents accidental deletion.
              </small>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ModuleCreation;
